import {API_BASE_URL} from '../config/apiConfig';

export interface PayrollPeriod {
    id: string;
    contractNumber: string;
    periodKey: string;
    startDate: string;
    endDate: string;
    status: string;
    createdAt: string;
    lockedAt: string;
}

export interface PayrollPeriodsResponse {
    totalRecords: number;
    payrollPeriods: PayrollPeriod[];
}

export interface AllPayrollPeriod {
    status: string;
    periodKey: string;
    value: string;
    /** Payroll workflow stage for this period: OPEN | CALCULATED | APPROVED | FINALIZED.
     *  Optional because older cached responses/tests may not include it yet. */
    workflowStage?: string;
}

export interface Branch {
    id: string;
    name: string;
}

export interface BranchesResponse {
    totalRecords: number;
    tenants: Branch[];
}

export interface Contract {
    firstName: string;
    lastName: string;
    contractId: string;
    contractNumber: string;
    startDate: string;
    endDate: string;
    status: string;
    personalNo?: string;
    employmentType?: string;
    jobPosition: string;
    payrollCalcType: string;
    withContribution: string;
    salaryBasis: string;
    baseAmount: string;
    workload: string;
    employerInsuranceBenefit: string;
    familyInsuranceDeduction: string;
    fundEnabled: string;
    notes: string;
}

export interface ContractsResponse {
    success: boolean;
    totalRecords: number;
    contracts: Contract[];
}

export interface CreatePeriodRequest {
    year: number;
    month: number;
}

export interface CreatePeriodResponse {
    success: boolean;
    message?: string;
}

export interface SnapshotResponse {
    success: boolean;
    message?: string;
}


export const getPayrollPeriods = async (token: string, year: number, month: number): Promise<PayrollPeriodsResponse> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/payroll/periods/get?year=${year}&month=${month}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to fetch payroll periods: ${response.statusText}`);
        }

        const data: PayrollPeriodsResponse = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching payroll periods:', error);
        throw error;
    }
};

export const createPayrollPeriod = async (token: string, year: number, month: number): Promise<CreatePeriodResponse> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/payroll/periods/add`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({year, month}),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to create payroll period: ${response.statusText}`);
        }

        const data: CreatePeriodResponse = await response.json();
        return data;
    } catch (error) {
        console.error('Error creating payroll period:', error);
        throw error;
    }
};

export const getAllPayrollPeriods = async (token: string): Promise<AllPayrollPeriod[]> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/payroll/periods/getAll`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to fetch all payroll periods: ${response.statusText}`);
        }

        const data: AllPayrollPeriod[] = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching all payroll periods:', error);
        throw error;
    }
};

export const getContractsByPeriodKey = async (token: string, periodKey: string): Promise<ContractsResponse> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/contract/periodKey/get?periodKey=${periodKey}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to fetch contracts: ${response.statusText}`);
        }

        const data: ContractsResponse = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching contracts by period key:', error);
        throw error;
    }
};

export const createSnapshot = async (token: string, periodKey: string | number, contractIds: string[]): Promise<SnapshotResponse> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/snapshot/add`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({periodKey, contractIds}),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to create snapshot: ${response.statusText}`);
        }

        const data: SnapshotResponse = await response.json();
        return data;
    } catch (error) {
        console.error('Error creating snapshot:', error);
        throw error;
    }
};

export const getBranches = async (token: string): Promise<BranchesResponse> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/branches/get`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to fetch branches: ${response.statusText}`);
        }

        const data: BranchesResponse = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching branches:', error);
        throw error;
    }
};

// ─── Payroll workflow (persisted batch runs: calculate → approve → finalize) ───────────────────
// Mirrors the backend's PayrollWorkflowService. A 409 response means a *conflict*, not a generic
// failure (e.g. stale inputs, or not every branch approved yet) — callers should surface the
// message to the user rather than treating it as an unexpected error.

export class PayrollWorkflowConflictError extends Error {}

export interface CalculateRunResponse {
    runId: string;
    periodKey: string;
    branchScope: string;
    status: string;
    inputHash: string;
    supersededPreviousRun: boolean;
    createdAt: string;
    calculation: unknown;
}

export interface ApproveRunResponse {
    runId: string;
    status: string;
    approvedAt: string;
    periodFullyApproved: boolean;
    periodWorkflowStage: string;
}

export interface FinalizePeriodResponse {
    periodKey: string;
    workflowStage: string;
    finalizedAt: string;
    finalizedRunIds: string[];
}

const parseWorkflowErrorMessage = async (response: Response, fallback: string): Promise<string> => {
    const contentType = response.headers.get('Content-Type') ?? '';
    try {
        if (contentType.includes('application/json')) {
            const data = await response.json();
            return data?.message || fallback;
        }
        const text = await response.text();
        return text || fallback;
    } catch {
        return fallback;
    }
};

export const calculatePayrollRun = async (token: string, periodKey: string, branch: string): Promise<CalculateRunResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/payroll/run/calculate?periodKey=${periodKey}&branch=${branch}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });
    if (response.status === 409) {
        throw new PayrollWorkflowConflictError(await parseWorkflowErrorMessage(response, 'Calculation conflict'));
    }
    if (!response.ok) {
        throw new Error(await parseWorkflowErrorMessage(response, `Failed to calculate payroll run: ${response.statusText}`));
    }
    return response.json();
};

export const approvePayrollRun = async (token: string, runId: string): Promise<ApproveRunResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/payroll/run/approve?runId=${runId}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });
    if (response.status === 409) {
        throw new PayrollWorkflowConflictError(await parseWorkflowErrorMessage(response, 'Approval conflict'));
    }
    if (!response.ok) {
        throw new Error(await parseWorkflowErrorMessage(response, `Failed to approve payroll run: ${response.statusText}`));
    }
    return response.json();
};

export const finalizePayrollPeriod = async (token: string, periodKey: string): Promise<FinalizePeriodResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/payroll/run/finalize?periodKey=${periodKey}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });
    if (response.status === 409) {
        throw new PayrollWorkflowConflictError(await parseWorkflowErrorMessage(response, 'Finalization conflict'));
    }
    if (!response.ok) {
        throw new Error(await parseWorkflowErrorMessage(response, `Failed to finalize payroll period: ${response.statusText}`));
    }
    return response.json();
};

export interface AtkReportFile {
    blob: Blob;
    filename: string;
}

/** Extracts the filename from a `Content-Disposition: attachment; filename="..."` header, falling
 *  back to `defaultFilename` when the header is missing/unparsable. */
const filenameFromContentDisposition = (response: Response, defaultFilename: string): string => {
    const contentDisposition = response.headers.get('Content-Disposition');
    if (!contentDisposition) return defaultFilename;
    const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return match?.[1] ?? defaultFilename;
};

export interface SupportedBankExport {
    bankId: string | null;
    bankName: string;
    code: string;
    format: string;
}

/** GET /api/payroll/bank-export/supported - banks that have an export template. */
export const fetchSupportedBankExports = async (token: string): Promise<SupportedBankExport[]> => {
    const response = await fetch(`${API_BASE_URL}/api/payroll/bank-export/supported`, {
        method: 'GET',
        headers: {'Authorization': `Bearer ${token}`},
    });
    if (!response.ok) {
        throw new Error(await parseWorkflowErrorMessage(response, `Failed to load supported banks: ${response.statusText}`));
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
};

/** Thrown for bank-export failures; `message` is the backend plain-text body ('' when absent). */
export class BankExportError extends Error {}

/**
 * GET /api/payroll/bank-export - bank-specific payroll list (xlsx/xls/csv, named by
 * Content-Disposition). Errors (400/403/404/409) carry a plain-text body, surfaced as the message.
 */
export const exportBankList = async (
    token: string,
    periodKey: string,
    branch: string,
    bank: {bankId?: string | null; bankName: string},
    fallbackFilename: string,
): Promise<AtkReportFile> => {
    const params = new URLSearchParams({periodKey, branch});
    if (bank.bankId) params.set('bankId', bank.bankId);
    else params.set('bankName', bank.bankName);
    const response = await fetch(`${API_BASE_URL}/api/payroll/bank-export?${params.toString()}`, {
        method: 'GET',
        headers: {'Authorization': `Bearer ${token}`},
    });
    if (!response.ok) {
        throw new BankExportError(await parseWorkflowErrorMessage(response, ''));
    }
    const blob = await response.blob();
    return {blob, filename: filenameFromContentDisposition(response, fallbackFilename)};
};

/**
 * GET /api/payroll/run/export/atk - the ATK-formatted Excel export (gated server-side: the
 * period must be FINALIZED, see `AllPayrollPeriod.workflowStage`). A 409 means the period isn't
 * finalized yet (plain-text body with a human-readable message) - callers should surface it to
 * the user rather than treating it as an unexpected failure, same convention as the other
 * workflow calls above.
 */
export const exportAtkReport = async (token: string, periodKey: string, branch: string): Promise<AtkReportFile> => {
    const response = await fetch(
        `${API_BASE_URL}/api/payroll/run/export/atk?periodKey=${periodKey}&branch=${branch}`,
        {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        },
    );
    if (response.status === 409) {
        throw new PayrollWorkflowConflictError(await parseWorkflowErrorMessage(response, 'Period is not finalized yet'));
    }
    if (!response.ok) {
        throw new Error(await parseWorkflowErrorMessage(response, `Failed to export ATK report: ${response.statusText}`));
    }
    const blob = await response.blob();
    const filename = filenameFromContentDisposition(response, `ATK-${periodKey}-${branch}.xls`);
    return {blob, filename};
};

